import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PromoCodeModal from './PromoCodeModal';

/** Where a code is parked while the customer goes off to sign in. */
export const PENDING_PROMO_CODE_KEY = 'pendingRedeemCode';

interface PromoCodeLauncherProps {
  /** Rendered instead of the default link, e.g. a styled button. */
  children?: (open: () => void) => React.ReactNode;
}

/**
 * "Have a promo code?" entry point, plus the other half of the sign-in
 * round-trip.
 *
 * Redeeming requires an account, so a signed-out customer with a code is sent
 * to sign in first. The code was already being parked in sessionStorage at
 * that point — but nothing ever read it back, and the modal navigated to
 * `/login?redirect=…` while LoginPage only honours router state. So after
 * signing in they landed on the home page with the code gone and no way to
 * tell what had happened. This picks the parked code back up and reopens the
 * modal on it.
 */
export const PromoCodeLauncher: React.FC<PromoCodeLauncherProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [initialCode, setInitialCode] = useState<string | undefined>();

  useEffect(() => {
    if (!isAuthenticated) return;
    let parked: string | null = null;
    try {
      parked = sessionStorage.getItem(PENDING_PROMO_CODE_KEY);
      if (parked) sessionStorage.removeItem(PENDING_PROMO_CODE_KEY);
    } catch {
      // Private browsing or blocked storage: nothing parked, nothing to do.
    }
    if (parked?.trim()) {
      setInitialCode(parked.trim());
      setIsOpen(true);
    }
  }, [isAuthenticated]);

  const open = () => {
    setInitialCode(undefined);
    setIsOpen(true);
  };

  return (
    <>
      {children ? (
        children(open)
      ) : (
        <button type="button" onClick={open} className="text-sm font-medium underline underline-offset-2 hover:no-underline">
          Have a promo code?
        </button>
      )}
      <PromoCodeModal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setInitialCode(undefined);
        }}
        initialCode={initialCode}
      />
    </>
  );
};

export default PromoCodeLauncher;
